import {
  DEFAULT_SERVICE_REMINDER_RULES,
  DEFAULT_SUPPLIER_REMINDER_RULES,
  type DefaultReminderRule,
  type ReminderRuleDto,
  type SupplierRenewalDto,
} from '@fos/shared/contracts/maintenance';
import { describeReminderRule } from '@fos/shared/labels';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Input } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';

interface Draft {
  enabled: boolean;
  days: string;
}

const isOverdue = (offsets: readonly number[]) => offsets.every((d) => d > 0);

/** Dni do pola: „30, 7” (przed) albo „7, 14, 21” (po terminie). */
function daysText(rule: Pick<ReminderRuleDto, 'offsetsDays'>): string {
  const overdue = isOverdue(rule.offsetsDays);
  return rule.offsetsDays
    .map((d) => (overdue ? d : -d))
    .sort((a, b) => (overdue ? a - b : b - a))
    .join(', ');
}

/** „30, 7” → [-30, -7]; „7, 14, 21” (po terminie) → [7, 14, 21]; błąd → null. */
function parseDays(text: string, overdue: boolean): number[] | null {
  const parts = text
    .split(/[,\s;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0 || parts.length > 20) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isInteger(n) || n < (overdue ? 1 : 0) || n > 365)) return null;
  const unique = [...new Set(nums)];
  return unique.map((n) => (overdue ? n : -n)).sort((a, b) => a - b);
}

/**
 * „Zmień” przypomnienia (karta zasobu → Przypomnienia): włącz / wyłącz regułę i zmień dni. Brakującą domyślną
 * regułę (np. do klienta) można dodać jednym kliknięciem. Do klienta zawsze mail przez Akceptacje; koszty
 * dostawców przypominają tylko zespołowi.
 */
export function RemindersDialog({
  open,
  onOpenChange,
  rules,
  serviceId,
  supplierRenewals,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rules: ReminderRuleDto[];
  serviceId: string | null;
  supplierRenewals: SupplierRenewalDto[];
}) {
  const utils = trpc.useUtils();
  const update = trpc.maintenance.updateReminderRule.useMutation();
  const create = trpc.maintenance.createReminderRule.useMutation();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setDrafts(Object.fromEntries(rules.map((r) => [r.id, { enabled: r.enabled, days: daysText(r) }])));
    setError(null);
  }, [open]);

  const serviceRules = rules.filter((r) => r.targetType === 'service');
  const missingDefaults: Array<{
    rule: DefaultReminderRule;
    target: { serviceId: string } | { supplierRenewalId: string };
  }> = [];
  if (serviceId) {
    for (const d of DEFAULT_SERVICE_REMINDER_RULES) {
      const exists = serviceRules.some(
        (r) => r.audience === d.audience && isOverdue(r.offsetsDays) === isOverdue(d.offsetsDays),
      );
      if (!exists) missingDefaults.push({ rule: d, target: { serviceId } });
    }
  }
  for (const s of supplierRenewals) {
    if (!rules.some((r) => r.supplierRenewalId === s.id)) {
      for (const d of DEFAULT_SUPPLIER_REMINDER_RULES)
        missingDefaults.push({ rule: d, target: { supplierRenewalId: s.id } });
    }
  }

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      for (const r of rules) {
        const d = drafts[r.id];
        if (!d) continue;
        const offsets = parseDays(d.days, isOverdue(r.offsetsDays));
        if (!offsets) {
          setError(`Popraw dni w regule „${describeReminderRule(r)}”: liczby oddzielone przecinkami, np. 30, 7.`);
          return;
        }
        const changedDays = offsets.join(',') !== [...r.offsetsDays].sort((a, b) => a - b).join(',');
        if (d.enabled === r.enabled && !changedDays) continue;
        await update.mutateAsync({
          id: r.id,
          version: r.version,
          ...(d.enabled !== r.enabled ? { enabled: d.enabled } : {}),
          ...(changedDays ? { offsetsDays: offsets } : {}),
        });
      }
      toast.success('Zapisano przypomnienia');
      await utils.maintenance.invalidate();
      onOpenChange(false);
    } catch (err) {
      setError(
        appErrorCode(err) === 'CONFLICT'
          ? 'Ktoś zmienił przypomnienia. Zamknij okno i spróbuj ponownie.'
          : errorMessage(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const addDefault = async (m: (typeof missingDefaults)[number]) => {
    setError(null);
    try {
      await create.mutateAsync({
        targetType: 'serviceId' in m.target ? 'service' : 'supplier_renewal',
        ...m.target,
        audience: m.rule.audience,
        offsetsDays: [...m.rule.offsetsDays],
        channel: m.rule.channel,
        maxRepeats: m.rule.maxRepeats,
      });
      await utils.maintenance.invalidate();
      toast.success('Dodano przypomnienie');
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const supplierLabel = (id: string | null) => {
    const s = supplierRenewals.find((x) => x.id === id);
    return s ? (s.notes ?? s.provider) : 'Koszt dostawcy';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Przypomnienia"
      className={FULLSCREEN_ON_PHONE}
      description="Wpisz, ile dni przed terminem (albo po terminie) przypominać. Mail do klienta zawsze czeka na Twoją zgodę w Akceptacjach."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={save} loading={saving}>
            Zapisz
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        {rules.length === 0 && <p className="py-2 text-body-s text-fg-muted">Brak przypomnień.</p>}
        {rules.map((r) => {
          const d = drafts[r.id] ?? { enabled: r.enabled, days: daysText(r) };
          const overdue = isOverdue(r.offsetsDays);
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-line py-3">
              <Checkbox
                checked={d.enabled}
                onCheckedChange={(v) => setDrafts((all) => ({ ...all, [r.id]: { ...d, enabled: v } }))}
                label={
                  <span>
                    {r.targetType === 'supplier_renewal' && (
                      <span className="text-fg-muted">{supplierLabel(r.supplierRenewalId)}: </span>
                    )}
                    {describeReminderRule(r)}
                  </span>
                }
                className="min-w-0 flex-1"
              />
              <span className="flex items-center gap-2 text-caption text-fg-muted">
                {overdue ? 'po terminie, dni' : 'dni przed'}
                <Input
                  size="s"
                  mono
                  value={d.days}
                  onChange={(e) => setDrafts((all) => ({ ...all, [r.id]: { ...d, days: e.target.value } }))}
                  className="w-[104px]"
                  aria-label={`Dni: ${describeReminderRule(r)}`}
                  disabled={!d.enabled}
                />
              </span>
            </div>
          );
        })}
        {missingDefaults.length > 0 && (
          <div className="mt-4 flex flex-col items-start gap-2">
            {missingDefaults.map((m) => (
              <Button
                key={`${'serviceId' in m.target ? m.target.serviceId : m.target.supplierRenewalId}-${m.rule.label}`}
                variant="ghost"
                size="s"
                icon={Plus}
                onClick={() => addDefault(m)}
                loading={create.isPending}
              >
                {'supplierRenewalId' in m.target ? `${supplierLabel(m.target.supplierRenewalId)}: ` : ''}
                {m.rule.label}
              </Button>
            ))}
          </div>
        )}
        {error && (
          <div className="mt-4">
            <FormMessage>{error}</FormMessage>
          </div>
        )}
      </div>
    </Dialog>
  );
}
